import React from "react";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import { useLocation } from "react-router";

import {
  OrganizationIcon,
  ProjectIcon,
  ConnectionIcon,
} from "@shell/components/icons";
import {
  ACCOUNT,
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { ALPHA } from "src/shell/styles/designTokens";

import { SidebarNavigationProps } from "../types/NavigationTypes";

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  matchFn?: (p: string) => boolean;
  onClick: () => void;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  collapsed = false,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias: activeOrgAlias } = useActiveOrganization();

  const isActive = (path: string, matchFn?: (p: string) => boolean) => {
    if (matchFn) return matchFn(location.pathname);
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const getItemStyles = (active: boolean) => {
    const base = {
      color: active ? theme.palette.primary.main : theme.palette.text.secondary,
      backgroundColor: active
        ? alpha(theme.palette.primary.main, ALPHA.NAV_ACTIVE_BG)
        : "transparent",
      height: 40,
      mb: "4px",
      py: 0,
      px: 1.5,
      position: "relative" as const,
      borderRadius: "8px",
      // S10/S11: hover lifts text to text.primary (active stays primary) and
      // uses a 0.06 background tint to match the mockup shell.
      "&:hover": {
        color: active ? theme.palette.primary.main : theme.palette.text.primary,
        backgroundColor: active
          ? alpha(theme.palette.primary.main, ALPHA.NAV_ACTIVE_HOVER)
          : alpha(theme.palette.primary.main, 0.06),
      },
      "&::before": active
        ? {
            content: '""',
            position: "absolute",
            left: collapsed ? -2 : -12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 3,
            height: 20,
            borderRadius: "0 3px 3px 0",
            backgroundColor: theme.palette.primary.main,
          }
        : {},
      // S9: align the nav item hover transition with the 200ms ease sidebar timing.
      transition: "background-color 200ms ease, color 200ms ease",
    };

    if (collapsed) {
      return {
        ...base,
        justifyContent: "center",
        width: 40,
        height: 40,
        minWidth: 0,
        mx: "auto",
        px: 0,
      };
    }

    return {
      ...base,
      justifyContent: "flex-start",
    };
  };

  const getIconStyles = (active: boolean) => ({
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    minWidth: collapsed ? 0 : 20,
    width: 20,
    height: 20,
    mr: collapsed ? 0 : 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 20,
    },
  });

  const primaryItems: NavItem[] =
    location.pathname === ORGANIZATIONS.ROOT.path
      ? []
      : [
          ...(activeOrgAlias
            ? [
                {
                  text: "Organization",
                  icon: <OrganizationIcon size={20} />,
                  path: ORGANIZATIONS.DETAILS.path,
                  matchFn: (p: string) =>
                    p.startsWith(ORGANIZATIONS.ROOT.path + "/") &&
                    !p.includes("/projects") &&
                    !p.includes("/connections") &&
                    !p.includes(
                      PLATFORM_PRODUCTS.CATALOG.path.split("/:orgAlias")[1],
                    ),
                  onClick: () =>
                    appNavigation.goToOrganizationDetails(activeOrgAlias),
                },
              ]
            : []),
          {
            text: "Projects",
            icon: <ProjectIcon size={20} />,
            path: ORGANIZATIONS.ROOT.path,
            matchFn: (p: string) => p.includes("/projects"),
            onClick: () => {
              const urlOrgAlias = location.pathname.match(
                /\/organizations\/([^/]+)/,
              )?.[1];
              const org = urlOrgAlias ?? activeOrgAlias;
              if (org) {
                appNavigation.goToProjects(org);
              } else {
                appNavigation.goToOrganizations();
              }
            },
          },
          {
            text: "Connections",
            icon: <ConnectionIcon size={20} />,
            path: ORGANIZATIONS.VCS_CONNECTIONS.path,
            matchFn: (p: string) => p.includes("/connections"),
            onClick: () => {
              const urlOrgAlias = location.pathname.match(
                /\/organizations\/([^/]+)/,
              )?.[1];
              const org = urlOrgAlias ?? activeOrgAlias;
              if (org) {
                appNavigation.goToVcsConnections(org);
              } else {
                appNavigation.goToOrganizations();
              }
            },
          },
        ];

  const accountItems: NavItem[] = [
    {
      text: "Profile",
      icon: <PersonOutlineIcon sx={{ fontSize: 20 }} />,
      path: ACCOUNT.PROFILE.path,
      onClick: () => appNavigation.goToProfile(),
    },
    {
      text: "Settings",
      icon: <SettingsIcon sx={{ fontSize: 20 }} />,
      path: ACCOUNT.SETTINGS.path,
      onClick: () => appNavigation.goToAccountSettings(),
    },
  ];

  const renderItem = (item: NavItem) => {
    const active = isActive(item.path, item.matchFn);
    return (
      <ListItem
        key={item.text}
        disablePadding
        sx={collapsed ? { justifyContent: "center" } : {}}
      >
        <Tooltip title={collapsed ? item.text : ""} placement="right" arrow>
          <ListItemButton
            onClick={item.onClick}
            aria-current={active ? "page" : undefined}
            sx={getItemStyles(active)}
          >
            <ListItemIcon sx={getIconStyles(active)}>{item.icon}</ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Primary navigation */}
      <Box component="nav" aria-label="Primary navigation">
        <List
          component="ul"
          sx={{ px: collapsed ? 0 : "12px", pt: 1, mb: "16px" }}
        >
          {primaryItems.map(renderItem)}
        </List>
      </Box>

      {/* Account navigation */}
      <Box component="nav" aria-label="Account navigation">
        <List component="ul" sx={{ px: collapsed ? 0 : "12px", pb: 1 }}>
          {accountItems.map(renderItem)}
        </List>
      </Box>
    </Box>
  );
};

export default SidebarNavigation;
